import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { Footer } from '../components/LegalDisclaimers';

export default function Terms() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14]">
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0B0E14] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2">
              <RendPlexLogo size="md" />
            </Link>
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <LanguageSwitcher variant="minimal" />
              <Link to="/">
                <Button variant="ghost" className="text-slate-600 dark:text-slate-400">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('terms.backToHome')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8 md:p-12">
          <h1 className="font-primary font-bold text-4xl text-slate-900 dark:text-white mb-2" data-testid="terms-title">
            {t('terms.title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">{t('terms.lastUpdated')}</p>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <div className="bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-400 p-6 mb-8">
              <h3 className="font-primary font-bold text-lg text-slate-900 dark:text-white mb-2">{t('terms.importantNotice')}</h3>
              <p className="text-slate-700 dark:text-slate-300 mb-2">{t('terms.byUsing')}</p>
              <ul className="list-disc pl-6 space-y-1 text-slate-700 dark:text-slate-300">
                <li>{t('terms.noticeItems.notAdvice')}</li>
                <li>{t('terms.noticeItems.estimates')}</li>
                <li>{t('terms.noticeItems.responsible')}</li>
                <li>{t('terms.noticeItems.consultPros')}</li>
                <li>{t('terms.noticeItems.noGuarantee')}</li>
              </ul>
            </div>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-4">{t('terms.keyTerms')}</h2>

            <h3 className="font-primary font-bold text-xl text-slate-900 dark:text-white mt-6 mb-3">{t('terms.section1Title')}</h3>
            <p className="text-slate-700 dark:text-slate-300 mb-4">{t('terms.section1Text')}</p>

            <h3 className="font-primary font-bold text-xl text-slate-900 dark:text-white mt-6 mb-3">{t('terms.section2Title')}</h3>
            <p className="text-slate-700 dark:text-slate-300 mb-4">{t('terms.section2Text')}</p>

            <h3 className="font-primary font-bold text-xl text-slate-900 dark:text-white mt-6 mb-3">{t('terms.section3Title')}</h3>
            <p className="text-slate-700 dark:text-slate-300 mb-4">{t('terms.section3Text')}</p>

            <h3 className="font-primary font-bold text-xl text-slate-900 dark:text-white mt-6 mb-3">{t('terms.section4Title')}</h3>
            <p className="text-slate-700 dark:text-slate-300 mb-4">{t('terms.section4Text')}</p>

            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-2xl p-6 mt-8">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                <strong>{t('terms.noteLabel')}</strong> {t('terms.noteText')}
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('terms.contactText')}{' '}
                <a href="mailto:support@rendplex.com" className="text-[#45C04A] underline">
                  support@rendplex.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
